// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {isNumber} from 'lodash';
import Tooltip from '@homeaway/react-tooltip';
import {UnitMessage as UrgencyMessage} from '@homeaway/react-urgency-messaging';
import GeoDistance from '../GeoDistance/GeoDistance';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {FormattedMessage} from 'react-intl';
import HitLabel from '../HitLabel/HitLabel';
import BadgeTooltip from './ReviewBadgeTooltip';
import PriceSummary from '@homeaway/react-price-summary';
import Experiment from '@homeaway/react-experiment';
import {VRBO_WEB_SERP_PRICE_TO_RIGHT} from '../../abacus/experiments';

import type {Node} from 'react';
import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    premierPartnerBadge: ?Badge,
    rankedBadges: Badge[],
    superlativesBadges: Badge[],
    isPriceAccurateAllowed: boolean
} & HitInfoProps;

type IconText = {
    icon: string,
    message: string,
    messageValueType: string
};

export type UnitMessage = {
    iconText: ?IconText
};

/**
 * This is the goofy, non-badge-looking PremierPartner label that typically
 * appears over the rating stars. Extracted locally to make
 * `showIncludesTaxesAndFeesMessage` logic easier to read.
 */
const PremierPartnerLabel = ({premierPartnerBadge, tooltipParentNode}) => (
    <Tooltip
        tooltipType="popover"
        content={premierPartnerBadge.helpText}
        placement="bottom"
        parentNode={tooltipParentNode}
        interactiveContainer
    >
        <span
            className="small"
            data-wdio="premier-partner-badge"
            tabIndex="-1"
            aria-hidden="true"
        >
            {premierPartnerBadge.name}
        </span>
    </Tooltip>
);

const hasSuperlativesBadges = (superlativesBadges): %checks =>
    !!superlativesBadges && !!superlativesBadges.length;

/**
 * Classnames' flow-typed definition doesn't export an input type, so we're
 * using the deprecated existential type (*) to work around writing our own.
 */
type SuperlativeProps = {
    className?: *,
    superlativesBadges: Badge[]
};
const Superlative = ({className, superlativesBadges}: SuperlativeProps) => {
    if (!superlativesBadges[0].helpText) {
        return (
            <span
                className={classNames('HitInfo__superlative small', className)}
                aria-hidden="true"
            >
                {superlativesBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span
                className={classNames('HitInfo__superlative small', className)}
                aria-hidden="true"
            >
                {superlativesBadges[0].name}
            </span>
            <BadgeTooltip helpText={superlativesBadges[0].helpText} />
        </span>
    );
};

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    brand,
    premierPartnerBadge,
    rankedBadges,
    hitIndex,
    intl,
    isHovered,
    listing,
    isDatelessSearch,
    isMetric,
    fullLocale,
    showIncludesTaxesAndFeesMessage,
    superlativesBadges,
    listingHasSecondaryPrice,
    isPriceAccurateAllowed,
    isMultiUnitHit
}: Props) {
    let tooltipParentNode;
    const {
        instantBookable,
        propertyMetadata,
        geoDistance,
        unitMessage,
        unitMetadata
    } = listing;
    const headline = isMultiUnitHit
        ? (unitMetadata && unitMetadata.unitName) || ''
        : (propertyMetadata && propertyMetadata.headline) || '';
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }

    let rateListing = filterGraphQlAnywhere(ratingFragments.listing, listing);
    rateListing.starSize = listingHasSecondaryPrice ? '12' : '16';

    const FooterExperiment = ({children, variant}) => (
        <Experiment
            name={VRBO_WEB_SERP_PRICE_TO_RIGHT}
            deferLogging
            variant={variant}
        >
            {children}
        </Experiment>
    );

    const PriceSuperlativeGroup = () => {
        return (
            <Fragment>
                {hasSuperlativesBadges(superlativesBadges) && (
                    <Superlative superlativesBadges={superlativesBadges} />
                )}
                <div
                    className={classNames('HitInfo__price', {
                        'HitInfo__Price--condensed': listingHasSecondaryPrice
                    })}
                >
                    <Price
                        listing={filterGraphQlAnywhere(
                            priceFragments.listing,
                            listing
                        )}
                        useAverageLabel={isDatelessSearch}
                        fullLocale={fullLocale}
                        instantBookable={instantBookable}
                        intl={intl}
                        isPriceAccurateAllowed={isPriceAccurateAllowed}
                        isDatelessSearch={isDatelessSearch}
                    />
                    {listingHasSecondaryPrice && listing.priceSummarySecondary && (
                        <span className="PriceSummary__secondary">
                            <PriceSummary
                                priceSummary={listing.priceSummarySecondary}
                            />
                        </span>
                    )}
                </div>
            </Fragment>
        );
    };

    const BadgeRatingGroup = () => {
        return (
            <Fragment>
                {premierPartnerBadge && (
                    <PremierPartnerLabel
                        premierPartnerBadge={premierPartnerBadge}
                        tooltipParentNode={tooltipParentNode}
                    />
                )}
                <div className="HitInfo__rating">
                    <Rating listing={rateListing} />
                </div>
            </Fragment>
        );
    };

    return (
        <Fragment>
            <div className="media-flex__body">
                {
                    <UrgencyMessage
                        className="HitInfo__content--urgency"
                        size="s"
                        unitMessage={unitMessage}
                        ariaHidden
                    />
                }
                <h2 className="HitInfo__headline hover-text" aria-hidden="true">
                    {headline}
                </h2>

                {isNumber(hitIndex) && (
                    <span className="sr-only">
                        <FormattedMessage
                            id="property"
                            description="Property"
                            defaultMessage="Property"
                        >
                            {(txt: Node): Node => (
                                <Fragment>
                                    {txt} {hitIndex + 1}
                                    {':'} {headline}
                                </Fragment>
                            )}
                        </FormattedMessage>
                    </span>
                )}

                <div className="HitInfo__details">
                    <Details
                        listing={listing}
                        isMetric={isMetric}
                        locale={intl.locale}
                    />
                </div>

                {geoDistance && (
                    <GeoDistance
                        brand={brand}
                        geoDistance={geoDistance}
                        isHighlighted={isHovered}
                    />
                )}
                {((showIncludesTaxesAndFeesMessage && premierPartnerBadge) ||
                    rankedBadges.length > 0) && (
                    <div className="HitInfo__labels">
                        {showIncludesTaxesAndFeesMessage &&
                            premierPartnerBadge && (
                                <HitLabel
                                    brand={brand}
                                    id={premierPartnerBadge.id}
                                    label={premierPartnerBadge.name}
                                    parentNode={tooltipParentNode}
                                    toolTipText={premierPartnerBadge.helpText}
                                />
                            )}
                        {rankedBadges.map((badge, index) => (
                            <HitLabel
                                key={index}
                                brand={brand}
                                id={badge.id}
                                label={badge.name}
                                toolTipText={badge.helpText}
                                parentNode={tooltipParentNode}
                            />
                        ))}
                    </div>
                )}
            </div>
            <footer className="media-flex__footer hover-bg">
                <FooterExperiment variant={0}>
                    <Fragment>
                        <div className="HitInfo__priceSuperlativeGroup--old">
                            <PriceSuperlativeGroup />
                        </div>
                        <div className="HitInfo__badgeRatingGroup--old">
                            <BadgeRatingGroup />
                        </div>
                    </Fragment>
                </FooterExperiment>
                <FooterExperiment variant={1}>
                    <Fragment>
                        <div className="HitInfo__badgeRatingGroup--new">
                            <BadgeRatingGroup />
                        </div>
                        <div className="HitInfo__priceSuperlativeGroup--new">
                            <PriceSuperlativeGroup />
                        </div>
                    </Fragment>
                </FooterExperiment>
            </footer>
        </Fragment>
    );
}
